/**
 * TimeBlocksAdmin.tsx — Admin > Appointments > Time Blocks.
 *
 * Legacy's Time Blocks list (admin_time_block_list.php) for the signed-in
 * account, in the Admin list layout: every block on the calendar, a recurring
 * block as one row, with legacy's columns —
 *   Date and Time (in the block's facility time zone, else the account's),
 *   Resources, Details (name, appointment type, facility), Type (Available /
 *   Not Available), Occurrence ("Once", or e.g. "Recurring until 2026-12-31
 *   every Mon, Wed") and Status (Required / Optional);
 * newest first; search by any of those. "Add Time Block" and a click on a row
 * open the scheduler's own block editor (BlockTimeModal), so a block edits the
 * same here as on the calendar; Delete removes the block and every recurrence.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column; a row still opens the block
 * editor, read-only (BlockTimeModal `readOnly`: values only, Close only).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { utcToWallClock } from '@/lib/facilityTimezone';
import BlockTimeModal from '@/pages/plans/BlockTimeModal';
import { listFacilities, type Facility } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import { appointmentsService, deleteBlockTime, getCalendarSettings } from '@/services/appointments.service';
import { listTimeBlocks, occurrenceText, type AdminTimeBlock } from '@/services/timeBlocksAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'date', header: 'Date', sortable: true, className: 'w-32' },
  { key: 'time', header: 'Time', className: 'w-40' },
  { key: 'resources', header: 'Resources', sortable: true },
  { key: 'details', header: 'Details' },
  { key: 'type', header: 'Type', sortable: true, className: 'w-32' },
  { key: 'occurrence', header: 'Occurrence', sortable: true },
  { key: 'status', header: 'Status', sortable: true, className: 'w-28' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** A block as the list shows it. */
interface BlockRow {
  block: AdminTimeBlock;
  /** YYYY-MM-DD HH:MM in the block's time zone, for sorting. */
  sortKey: string;
  date: string;
  time: string;
  resources: string;
  details: { label: string; value: string }[];
  occurrence: string;
}

function to12h(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function longDate(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

const SORT: ListSortCompare<BlockRow> = {
  date: (a, b) => compareText(a.sortKey, b.sortKey),
  resources: (a, b) => compareText(a.resources, b.resources),
  type: (a, b) => Number(b.block.available) - Number(a.block.available),
  occurrence: (a, b) => compareText(a.occurrence, b.occurrence),
  status: (a, b) => Number(b.block.required) - Number(a.block.required),
};

export function TimeBlocksAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [blocks, setBlocks] = useState<AdminTimeBlock[]>([]);
  const [resourceNames, setResourceNames] = useState<Map<number, string>>(new Map());
  const [typeNames, setTypeNames] = useState<Map<number, string>>(new Map());
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [accountTz, setAccountTz] = useState('America/Los_Angeles');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ id?: number } | null>(null);
  const [deleting, setDeleting] = useState<BlockRow | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [list, resources, types, facs, settings] = await Promise.all([
        listTimeBlocks(),
        appointmentsService.getResources().catch(() => []),
        appointmentsService.getAppointmentTypes().catch(() => []),
        listFacilities().catch(() => [] as Facility[]),
        getCalendarSettings().catch(() => null),
      ]);
      setBlocks(list);
      setResourceNames(new Map(resources.map((r) => [r.resourceId, r.resourceName])));
      setTypeNames(new Map(types.map((t) => [t.appointmentTypeId, t.appointmentTypeName])));
      setFacilities(Array.isArray(facs) ? facs : []);
      if (settings?.accountIanaTimezone) setAccountTz(settings.accountIanaTimezone);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load the time blocks'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo((): BlockRow[] => {
    const facilityById = new Map(facilities.map((f) => [f.facilityId, f]));
    return blocks.map((block) => {
      const facility = block.facilityId ? facilityById.get(block.facilityId) : undefined;
      const tz = facility?.ianaTimezone || accountTz;
      const start = utcToWallClock(block.start, tz);
      const end = utcToWallClock(block.end, tz);
      const details: { label: string; value: string }[] = [];
      if (block.title && block.title !== 'Blocked Time') details.push({ label: 'Name', value: block.title });
      const typeName = block.appointmentTypeId ? typeNames.get(block.appointmentTypeId) : undefined;
      if (typeName) details.push({ label: 'Appointment Type', value: typeName });
      if (facility) details.push({ label: 'Facility', value: facility.facilityName });
      return {
        block,
        sortKey: start ? `${start.date} ${start.time}` : '',
        date: start ? longDate(start.date) : '—',
        time: start && end ? `${to12h(start.time)} - ${to12h(end.time)}` : '—',
        resources: block.resourceIds.map((id) => resourceNames.get(id) ?? `Resource #${id}`).join(', '),
        details,
        occurrence: occurrenceText(block),
      };
    });
  }, [blocks, facilities, accountTz, typeNames, resourceNames]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      query === ''
        ? rows
        : rows.filter((r) =>
            [r.date, r.time, r.resources, r.occurrence, r.block.available ? 'available' : 'not available', r.block.required ? 'required' : 'optional', ...r.details.map((d) => d.value)].some(
              (v) => v.toLowerCase().includes(query),
            ),
          ),
    [rows, query],
  );
  const sorting = useListSort(filtered, { initialKey: 'date', initialDir: 'desc', compare: SORT });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query] });

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteBlockTime(deleting.block.id);
      setBlocks((prev) => prev.filter((b) => b.id !== deleting.block.id));
      toast.success('Time block deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete the time block'));
    }
  }

  return (
    <AdminListPage
      title="Time Blocks"
      subtitle="Blocked and open time on the calendar"
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({})}>
          Add Time Block
        </Button>
      }
      toolbar={<AdminListToolbar search={search} onSearchChange={setSearch} searchPlaceholder="Search time blocks..." searchLabel="Search time blocks" />}
    >
      {loading ? (
        <SpinnerOverlay label="Loading time blocks…" />
      ) : filtered.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No time blocks found' : 'No time blocks yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a time block to block or open time on the calendar.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Time blocks" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((r) => (
              <tr key={r.block.id} data-block-id={r.block.id} className={adminRowClass({ clickable: true })} onClick={() => setEditing({ id: r.block.id })}>
                <td className={`${ADMIN_TD_CLASS} whitespace-nowrap`}>{r.date}</td>
                <td className={`${ADMIN_TD_CLASS} whitespace-nowrap`}>{r.time}</td>
                <td className={ADMIN_TD_CLASS}>{r.resources || <span className="text-slate-400">None</span>}</td>
                <td className={`${ADMIN_TD_CLASS} text-xs`}>
                  {r.details.length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    r.details.map((d) => (
                      <div key={d.label}>
                        <span className="font-medium text-slate-600">{d.label}:</span> {d.value}
                      </div>
                    ))
                  )}
                </td>
                <td className={ADMIN_TD_CLASS}>
                  {r.block.available ? (
                    <OutlineBadge tone="green">Available</OutlineBadge>
                  ) : (
                    <OutlineBadge tone="red">Not Available</OutlineBadge>
                  )}
                </td>
                <td className={`${ADMIN_TD_CLASS} text-xs`}>{r.occurrence}</td>
                <td className={ADMIN_TD_CLASS}>{r.block.required ? 'Required' : 'Optional'}</td>
                {!locked && (
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowActions>
                      <AdminRowAction kind="edit" label={`Edit time block of ${r.date}`} onClick={() => setEditing({ id: r.block.id })} />
                      <AdminRowAction kind="delete" label={`Delete time block of ${r.date}`} onClick={() => setDeleting(r)} />
                    </AdminRowActions>
                  </td>
                )}
              </tr>
            ))}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['time block', 'time blocks']} />
        </>
      )}

      {editing && (
        <BlockTimeModal
          open
          editBlockId={editing.id}
          readOnly={locked}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete this time block?"
        message={deleting?.block.recurring ? 'The block and every one of its recurrences come off the calendar.' : 'The block comes off the calendar.'}
        confirmLabel="Delete time block"
        variant="danger"
      />
    </AdminListPage>
  );
}
